import classNames from "classnames/bind";
import React, { useRef, type KeyboardEventHandler } from "react";
import { Card } from "../Card";
import { Skeleton } from "../Skeleton";
import styles from "./GridCard.module.scss";
import type { GridCardContainerProps, GridCardFooterProps } from "./types";

const cx = classNames.bind(styles);

const GridCardContainer = ({
  title,
  subtitle,
  description,
  onClick,
  colorVariant = "default",
  isLoading = false,
  isBusy = false,
  slotFooter,
}: GridCardContainerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (evt) => {
    if (evt.code !== "Enter") return;

    onClick?.();
  };

  const CustomTagName = onClick ? "a" : "div";

  return (
    <Card
      style={{ padding: 0 }}
      className={cx("container")}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={containerRef}
    >
      <CustomTagName
        className={cx("content-container")}
        onClick={onClick}
      >
        <Skeleton
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <p className={cx("title", { isLink: !!onClick })}>{title}</p>
        </Skeleton>

        {subtitle ? (
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
          >
            <p className={cx("subtitle")}>{subtitle}</p>{" "}
          </Skeleton>
        ) : null}

        {description ? (
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
          >
            <p className={cx("description")}>{description}</p>
          </Skeleton>
        ) : null}
      </CustomTagName>

      {slotFooter ? slotFooter({ isLoading, isBusy, colorVariant }) : null}
    </Card>
  );
};

const GridCardFooter = ({
  slotLeft,
  slotRight,
  colorVariant,
  className,
  isLoading = false,
  isBusy = false,
  ...restProps
}: GridCardFooterProps) => {
  return (
    <div
      className={cx("footer", !isLoading && colorVariant, className)}
      {...restProps}
    >
      {slotLeft
        ? slotLeft({ className: cx("left-metadata"), isLoading, isBusy })
        : null}
      {slotRight
        ? slotRight({ className: cx("right-metadata"), isLoading, isBusy })
        : null}
    </div>
  );
};

GridCardContainer.displayName = "GridCardContainer";
GridCardFooter.displayName = "GridCardFooter";

const Container = GridCardContainer;
const Footer = GridCardFooter;

export const GridCard = {
  Container,
  Footer,
};

export { GridCardContainer, GridCardFooter };
